import React, { useEffect, useMemo, useState } from "react"
import { withBase } from "constants/basePath"
import MetaTags from "react-meta-tags"
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Form,
  Label,
  FormFeedback,
  Alert,
  Button,
  Spinner,
} from "reactstrap"
import { useFormik } from "formik"

import { changeOwnPassword } from "../../api"
import { clearSession } from "../../helpers/auth_helpers"
import {
  MIN_LENGTH,
  buildValidationSchema,
} from "../../helpers/password_policy_helpers"
import PasswordInput from "components/Input/PasswordInput"
import PasswordPolicyChecklist from "components/Input/PasswordPolicyChecklist"
import userStore from "userStore"

const CambiarClave = () => {
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [listo, setListo] = useState(false)

  // BDSJEA-230: el username sale del store (poblado async por getUser() en
  // App.js via whoami). La regla "no puede contener el usuario" necesita
  // esta referencia externa a values, por eso el schema se arma aca adentro
  // con useMemo en vez de ser una constante module-level: asi Formik siempre
  // valida (tecleo/blur/submit) contra el username mas fresco. Ver comentario
  // en el useEffect de abajo para el caso en que el username llega DESPUES de
  // que el campo ya fue tocado.
  const username = userStore(state => state.user?.username)
  const validationSchema = useMemo(
    () => buildValidationSchema(username),
    [username],
  )

  const validation = useFormik({
    initialValues: {
      claveActual: "",
      claveNueva: "",
      claveNuevaRepetida: "",
    },
    validationSchema,
    onSubmit: values => {
      setError("")
      setLoading(true)

      changeOwnPassword(values)
        .then(() => {
          setListo(true)
          // Cambiar la clave invalida la sesión a propósito: el token viejo
          // sigue siendo criptográficamente válido, así que se fuerza un login
          // nuevo para que quede claro cuál es la credencial vigente.
          setTimeout(() => {
            clearSession()
            window.location.href = withBase("/iniciar-sesion")
          }, 2500)
        })
        .catch(err => {
          setError(err.message)
          setLoading(false)
        })
    },
  })

  // BDSJEA-230: el whoami (App.js) puede resolver DESPUES de que el usuario
  // ya tipeo y toco el campo "Clave nueva" -- sobre todo en el flujo de
  // clave provisoria, donde Login.js hace un window.location.href que
  // recarga toda la app y vuelve a disparar el whoami desde cero. El
  // checklist (PasswordPolicyChecklist) ya es reactivo porque recalcula en
  // cada render, pero Formik NO revalida solo porque cambio una prop
  // externa (username): solo revalida en handleChange/handleBlur/submit. Sin
  // este efecto, el checklist se pondria en rojo al instante pero el borde
  // del input y el FormFeedback quedarian "limpios" hasta la proxima tecla o
  // blur -- misma logica que el patron ya usado en
  // pages/Agro/Siembra/FormSiembra.js para disparar validateField desde un
  // trigger externo al propio input.
  //
  // Nota: si hubiera una validacion full-form todavia en vuelo (disparada
  // por un blur/tecla previo, con el schema viejo) que resuelve DESPUES de
  // este validateField puntual, esa respuesta tardia pisaria el error que
  // seteamos aca (Formik no descarta resultados de validaciones stale). En
  // la practica no es alcanzable: la validacion de Yup resuelve en el mismo
  // tick/microtask (no hay I/O real), mientras que el whoami es una llamada
  // de red real, siempre mucho mas lenta -- para cuando llega, cualquier
  // validacion previa ya asento.
  useEffect(() => {
    if (validation.touched.claveNueva) validation.validateField("claveNueva")
    // Nota: el proyecto no tiene el plugin eslint react-hooks instalado (no
    // hay regla exhaustive-deps activa), asi que no hace falta un
    // eslint-disable aca -- agregar uno de todos modos genera el warning
    // "Definition for rule ... was not found" en el build. La dependencia es
    // intencionalmente solo [username]: revalidar en cada cambio de
    // claveActual/claveNuevaRepetida ya lo cubren sus propios
    // onChange/onBlur.
  }, [username])

  const campo = (name, label, autoComplete) => (
    <div className="mb-3">
      <Label className="form-label">{label}</Label>
      <PasswordInput
        name={name}
        autoComplete={autoComplete}
        onChange={validation.handleChange}
        onBlur={validation.handleBlur}
        value={validation.values[name]}
        invalid={validation.touched[name] && !!validation.errors[name]}
        disabled={loading || listo}
      />
      {validation.touched[name] && validation.errors[name] ? (
        // BDSJEA-229: el wrapper position:relative de PasswordInput agrega
        // una capa de anidamiento entre el Input y este FormFeedback (igual
        // que ya le pasaba a Login.js con InputGroup), asi que el selector
        // ".is-invalid ~ .invalid-feedback" de Bootstrap no aplica porque ya
        // no son hermanos directos. Se fuerza display:block, mismo patron
        // que usa Login.js.
        <FormFeedback type="invalid" style={{ display: "block" }}>
          {validation.errors[name]}
        </FormFeedback>
      ) : null}
    </div>
  )

  return (
    <React.Fragment>
      <MetaTags>
        <title>Cambiar clave | EWAN</title>
      </MetaTags>
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card>
                <CardBody className="p-4">
                  <h5 className="text-primary">Cambiar clave</h5>
                  <p className="text-muted">
                    La clave debe tener al menos {MIN_LENGTH} caracteres,
                    combinar mayúsculas, minúsculas y números, y no puede
                    contener tu nombre de usuario. Tampoco puede repetir ninguna
                    de las últimas cinco.
                  </p>

                  {error ? <Alert color="danger">{error}</Alert> : null}
                  {listo ? (
                    <Alert color="success">
                      Clave actualizada. Te vamos a pedir que inicies sesión de
                      nuevo.
                    </Alert>
                  ) : null}

                  <Form
                    onSubmit={e => {
                      e.preventDefault()
                      validation.handleSubmit()
                      return false
                    }}
                  >
                    {campo("claveActual", "Clave actual", "current-password")}
                    {campo("claveNueva", "Clave nueva", "new-password")}
                    {validation.values.claveNueva ||
                    validation.touched.claveNueva ? (
                      <PasswordPolicyChecklist
                        value={validation.values.claveNueva}
                        context={{
                          username,
                          claveActual: validation.values.claveActual,
                        }}
                      />
                    ) : null}
                    {campo(
                      "claveNuevaRepetida",
                      "Repetir clave nueva",
                      "new-password",
                    )}

                    <div className="mt-3 d-grid">
                      <Button
                        color="primary"
                        type="submit"
                        disabled={loading || listo}
                      >
                        {loading ? <Spinner size="sm" /> : "Cambiar clave"}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default CambiarClave
